// BILL-03 — the frontend's view of the server's subscription verdict.
//
// The server is the gate (HTTP guard, WS router, MCP); this store only decides
// which SHELL to render, so a stale tab degrades to the paywall instead of to a
// stream of refused requests. It reads GET /api/v1/billing/entitlement, which is
// served from the same EntitlementCache the server gates on, so the FE and the
// server cannot disagree about who is entitled.
//
// NOT persisted: the verdict is re-read on every load, and a persisted verdict
// would outlive a logout (so the `lvdr_session_` rule does not apply). It is
// instead reset whenever the signed-in user changes or the session ends, so
// user B never inherits user A's verdict (see `bindToAuth`).
//
// BILL-20a: the same body also carries the server's best-of gate tier
// (`gateTier`) and the capability map (`capabilities`) the frontend reads
// through useCapability. Both come from the same gate-tier read the server
// gates on; the dashboard layout refreshes them on `tierChangedEvent`.
//
// Design: docs/plans/2026-09-28-bill03-server-subscription-gate-design.md §6.
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { apiFetch } from '@/lib/http';
import { isSubscriptionRequired } from '@/lib/errors/catalog';
import { useAuthStore } from '@/stores/authStore';

export const ENTITLEMENT_PATH = '/api/v1/billing/entitlement';
const LOAD_TIMEOUT_MS = 30_000;

/** One capability's verdict in GET /billing/entitlement — BILL-20a-1's
 *  billing.CapabilityView (snake_case wire, like the rest of this body). */
export interface CapabilityView {
  entitled: boolean;
  required_tier?: string;
}

/** Wire shape of GET /api/v1/billing/entitlement (pinned contract; snake-free keys). */
export interface EntitlementResponse {
  entitled: boolean;
  exempt: boolean;
  enforced: boolean;
  status: string;
  source: string;
  reason: EntitlementReason | string;
  /** BILL-20a: best-of gate tier (D11) — NOT the display tier, which stays
   *  subscriptionStore.tierName. */
  gate_tier?: string;
  /** BILL-20a: capability id → verdict from the same gate-tier read (D11).
   *  Absent ⇒ unknown, never entitled. */
  capabilities?: Record<string, CapabilityView>;
}

export type EntitlementReason =
  | 'entitled'
  | 'exempt'
  | 'demo'
  | 'not_entitled'
  | 'lkg'
  | 'resolver_error'
  | 'disarmed';

export type EntitlementStatus = 'idle' | 'loading' | 'ready' | 'error';

/** Which shell the dashboard renders. */
export type EntitlementView = 'pending' | 'app' | 'paywall' | 'unverified';

interface EntitlementState {
  status: EntitlementStatus;
  /** A background re-check is in flight; the current verdict stays in force. */
  refreshing: boolean;
  entitled: boolean;
  exempt: boolean;
  enforced: boolean;
  reason: string;
  /** BILL-20a: the server's best-of gate tier; '' when none was sent. */
  gateTier: string;
  /** BILL-20a: capability id → verdict. null = the server sent no map
   *  (pre-20a backend, demo guest, fetch never succeeded). */
  capabilities: Record<string, CapabilityView> | null;
  actions: {
    load: () => Promise<void>;
    refresh: () => Promise<void>;
    markRequired: () => void;
  };
}

type Data = Omit<EntitlementState, 'actions'>;

/**
 * The verdict for a `/demo` guest, decided locally.
 *
 * The server's demo gate refuses /api/v1/billing/entitlement for a guest token
 * (it is account-scoped), so asking can only ever fail — and a failure renders
 * "couldn't verify your plan" over the one surface whose job is to show the
 * product working. The server exempts guests from the subscription gate on
 * every transport (HTTP scope, WS `conn.DemoReadOnly`), so this answers what
 * the server would. It is not an access decision: the FE never grants anything.
 */
const DEMO_VERDICT: Pick<
  Data,
  'entitled' | 'exempt' | 'enforced' | 'reason' | 'gateTier' | 'capabilities'
> = {
  entitled: true,
  exempt: true,
  enforced: true,
  reason: 'demo',
  gateTier: '',
  capabilities: null,
};

function isDemoGuestSession(): boolean {
  return useAuthStore.getState().isDemoGuest;
}

const INITIAL: Data = {
  status: 'idle',
  refreshing: false,
  entitled: false,
  exempt: false,
  enforced: true,
  reason: '',
  gateTier: '',
  capabilities: null,
};

// Generation counter: bumped by every reset, so a response that was requested
// for a previous user/session can never write into the current one.
let generation = 0;
let inflight: AbortController | null = null;
/** D-P12: a refresh() that arrived while the verdict was already being read. */
let refreshQueued = false;

/** Run the ONE queued follow-up, but only for the session that queued it. */
function drainQueuedRefresh(gen: number): void {
  if (gen !== generation || !refreshQueued) return;
  refreshQueued = false;
  void useEntitlementStore.getState().actions.refresh();
}

function isVerdict(v: unknown): v is EntitlementResponse {
  if (typeof v !== 'object' || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    typeof o.entitled === 'boolean' &&
    typeof o.exempt === 'boolean' &&
    typeof o.enforced === 'boolean' &&
    typeof o.reason === 'string'
  );
}

/** One fetch with a 30 s timeout and supersession. Throws on any failure. */
async function fetchVerdict(): Promise<EntitlementResponse> {
  inflight?.abort();
  const controller = new AbortController();
  inflight = controller;
  const timer = setTimeout(() => controller.abort(), LOAD_TIMEOUT_MS);
  try {
    const res = await apiFetch<unknown>(ENTITLEMENT_PATH, { signal: controller.signal });
    if (!isVerdict(res)) throw new Error('entitlement: malformed response');
    return res;
  } finally {
    clearTimeout(timer);
    if (inflight === controller) inflight = null;
  }
}

export const useEntitlementStore = create<EntitlementState>()(
  devtools(
    subscribeWithSelector(
      immer((set, get) => ({
        ...INITIAL,
        actions: {
          load: async () => {
            bindToAuth();
            if (isDemoGuestSession()) {
              set((s) => {
                Object.assign(s, DEMO_VERDICT);
                s.status = 'ready';
                s.refreshing = false;
              });
              return;
            }
            const { status } = get();
            if (status === 'ready') return get().actions.refresh();
            if (status === 'loading') return;
            const gen = generation;
            try {
              set((s) => {
                s.status = 'loading';
              });
              try {
                const v = await fetchVerdict();
                if (gen !== generation) return;
                set((s) => {
                  Object.assign(s, pick(v));
                  s.status = 'ready';
                });
              } catch {
                if (gen !== generation) return;
                set((s) => {
                  s.status = 'error';
                });
              }
            } finally {
              drainQueuedRefresh(gen);
            }
          },
          // Re-reads the verdict in the background (the current one stays in
          // force). Coalesces (D-P12): a call that arrives while a read is
          // already in flight queues ONE follow-up instead of being dropped.
          refresh: async () => {
            const { status } = get();
            if (isDemoGuestSession()) return get().actions.load();
            // A retry from 'error' keeps status 'error' while in flight: going
            // through load() would set 'loading', which the dashboard layout
            // renders as 'pending' — unmounting PaywallShell and, with it,
            // AppConnection's WebSocket.
            // D-P12: the request in flight may have been answered before the
            // change that triggered this call (tierChangedEvent, checkout
            // return) — queue ONE follow-up instead of swallowing it.
            if (status === 'loading' || get().refreshing) {
              refreshQueued = true;
              return;
            }
            if (status !== 'ready' && status !== 'error') return get().actions.load();
            const gen = generation;
            try {
              set((s) => {
                s.refreshing = true;
              });
              try {
                const v = await fetchVerdict();
                if (gen !== generation) return;
                set((s) => {
                  Object.assign(s, pick(v));
                  s.status = 'ready';
                  s.refreshing = false;
                });
              } catch (err) {
                if (gen !== generation) return;
                // Keep the previous verdict: a failed re-check must neither lock
                // out a payer nor unlock an unpaid user.
                console.warn('[entitlement] refresh failed; keeping previous verdict', err);
                set((s) => {
                  s.refreshing = false;
                });
              }
            } finally {
              drainQueuedRefresh(gen);
            }
          },
          markRequired: () => {
            bindToAuth();
            // An unverified verdict (fetch error, or the server's own
            // resolver_error) stays unverified: PaywallShell's AppConnection
            // draws a burst of WS 240s on connect, and flipping to not_entitled
            // on them would send a possibly-paying user to /plans during an
            // outage (design §6: never a redirect on an unverifiable plan).
            if (entitlementView(get()) === 'unverified') return;
            set((s) => {
              s.entitled = false;
              s.exempt = false;
              s.enforced = true;
              s.reason = 'not_entitled';
              s.status = 'ready';
            });
          },
        },
      })),
    ),
    { name: 'EntitlementStore' },
  ),
);

function pick(
  v: EntitlementResponse,
): Pick<Data, 'entitled' | 'exempt' | 'enforced' | 'reason' | 'gateTier' | 'capabilities'> {
  return {
    entitled: v.entitled,
    exempt: v.exempt,
    enforced: v.enforced,
    reason: v.reason,
    gateTier: typeof v.gate_tier === 'string' ? v.gate_tier : '',
    capabilities: parseCapabilities(v.capabilities),
  };
}

/** Keep only well-formed entries: the map gates UI, so a malformed entry must
 *  read as "unknown" (absent), never as a verdict. Not an object ⇒ null. */
function parseCapabilities(raw: unknown): Record<string, CapabilityView> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const out: Record<string, CapabilityView> = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v !== 'object' || v === null) continue;
    const o = v as Record<string, unknown>;
    if (typeof o.entitled !== 'boolean') continue;
    const view: CapabilityView = { entitled: o.entitled };
    if (typeof o.required_tier === 'string' && o.required_tier)
      view.required_tier = o.required_tier;
    out[id] = view;
  }
  return out;
}

// The real actions, captured once so a reset also undoes a test's stubbed
// `actions` (production never replaces them).
const ACTIONS = useEntitlementStore.getState().actions;

/** Drop the verdict and any in-flight request (logout, user switch, tests). */
export function resetEntitlementStore(): void {
  generation++;
  refreshQueued = false;
  inflight?.abort();
  inflight = null;
  useEntitlementStore.setState({ ...INITIAL, actions: ACTIONS });
}

// Reset on logout / user switch. Bound lazily (on the first load or
// markRequired) rather than at module load: authStore sits inside an import
// cycle (authStore → hardLogout → workspaceStore → authStore), and touching it
// during this module's evaluation could hit it before it is initialised.
let authBound = false;
function bindToAuth(): void {
  if (authBound) return;
  authBound = true;
  useAuthStore.subscribe((next, prev) => {
    const userChanged = (next.user?.id ?? null) !== (prev.user?.id ?? null);
    const leftAuthenticated = prev.status === 'authenticated' && next.status !== 'authenticated';
    const demoChanged = next.isDemoGuest !== prev.isDemoGuest;
    if (userChanged || leftAuthenticated || demoChanged) resetEntitlementStore();
  });
}

/**
 * entitlementView maps the store to the shell the dashboard renders:
 *  - `pending` until the first verdict — the layout shows a neutral loader and
 *    mounts NEITHER shell, so AppConnection connects once;
 *  - `app` when not enforced (kill switch), entitled, or exempt;
 *  - `unverified` on a fetch error or a resolver_error verdict — the "couldn't
 *    verify your plan" panel, never a redirect to /plans;
 *  - otherwise `paywall`.
 */
export function entitlementView(s: Pick<Data, 'status' | 'entitled' | 'exempt' | 'enforced' | 'reason'>): EntitlementView {
  if (s.status === 'idle' || s.status === 'loading') return 'pending';
  if (s.status === 'error') return 'unverified';
  if (!s.enforced || s.entitled || s.exempt) return 'app';
  if (s.reason === 'resolver_error') return 'unverified';
  return 'paywall';
}

/** True for the server's SUBSCRIPTION_REQUIRED refusal (HTTP 402 / WS code 240). */
export { isSubscriptionRequired };
